/**
 * Re-arms the cart drawer's focus trap after an in-drawer add-to-cart.
 *
 * Dawn's product-form.js publishes PUB_SUB_EVENTS.cartUpdate (source: 'product-form')
 * and then calls cart-drawer.js renderContents(), which replaces #CartDrawer's inner
 * HTML and calls open() — but open() returns early when the drawer is already
 * `.active`, so the trap installed on the OLD nodes is orphaned and the submit
 * button that had focus is gone (focus falls to <body>, Tab escapes the dialog).
 *
 * A second, independent re-render fires on the same event: cart.js's
 * CartItems#onCartUpdate (inherited by <cart-drawer-items>) does its own async
 * fetch of `?section_id=cart-drawer` and replaces <cart-drawer-items> and
 * .cart-drawer__footer again once that resolves — on top of renderContents'
 * own (synchronous) replacement. A single re-arm on a fixed delay races that
 * fetch: on a fast round trip it can resolve AFTER our delay, silently
 * orphaning the elements we just trapped focus around and letting Tab escape
 * the dialog again. A MutationObserver on #CartDrawer re-arms every time its
 * subtree actually changes while the drawer is open, so the trap always ends
 * up bound to whichever nodes are live last, regardless of which re-render
 * wins the race.
 *
 * Separately, cart-drawer.js's close() calls removeTrapFocus(this.activeElement)
 * and returns focus there. If the drawer was opened from a PDP add-to-cart button
 * (or any opener) that later gets re-rendered away or detached, activeElement is
 * stale and focus silently falls to <body> on close. ensureActiveElement() below
 * repoints activeElement at the header cart icon whenever it's missing or detached,
 * both on every cartUpdate (any source) and inside rearmFocusTrap.
 */
(function () {
  if (
    typeof subscribe === 'function' &&
    typeof trapFocus === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    let settleTimer;

    function ensureActiveElement() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      if (!drawer.activeElement || !document.contains(drawer.activeElement)) {
        drawer.setActiveElement(document.getElementById('cart-icon-bubble'));
      }
    }

    function rearmFocusTrap() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.classList.contains('active')) return;
      ensureActiveElement();
      const container = drawer.classList.contains('is-empty')
        ? drawer.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusTarget = drawer.querySelector('.drawer__inner');
      if (container && focusTarget) trapFocus(container, focusTarget);
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      ensureActiveElement();

      if (!event || event.source !== 'product-form') return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.classList.contains('active')) return;

      const scheduleRearm = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(rearmFocusTrap, 50);
      };

      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) {
        const observer = new MutationObserver(scheduleRearm);
        observer.observe(cartDrawerEl, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 2000);
      }

      scheduleRearm();
    });
  }
})();
